import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface SearchArtistItem {
  id: number;
  name: string;
  slug: string;
  imageUrl: string | null;
  genres: string[];
  verified: boolean;
}

export interface SearchSongArtistCredit {
  id: number | null;
  name: string;
}

export interface SearchSongItem {
  id: number;
  title: string;
  imageUrl: string | null;
  artists: SearchSongArtistCredit[];
  releaseDate: string | null;
  durationMs: number | null;
  streams: number | null;
  ifpiWeeks?: number;
}

export interface SearchAlbumItem {
  id: number;
  title: string;
  type: string | null;
  imageUrl: string | null;
  releaseDate: string | null;
  label: string | null;
  totalTracks: number | null;
  artists: SearchSongArtistCredit[];
}

export interface SearchResult {
  artists: SearchArtistItem[];
  songs: SearchSongItem[];
  albums: SearchAlbumItem[];
}

interface ApiResponse<T> {
  status: string;
  data: T;
}

@Injectable({ providedIn: 'root' })
export class SearchService {
  private http = inject(HttpClient);
  private searchUrl = `${environment.apiUrl}/search`;

  /**
   * Search the music catalog for artists, songs, and albums.
   */
  search(query: string, limit = 6, type: 'all' | 'artists' | 'songs' | 'albums' = 'all'): Observable<SearchResult> {
    const cleanQuery = query.trim();
    if (!cleanQuery) {
      return of({ artists: [], songs: [], albums: [] });
    }

    const params = new HttpParams()
      .set('q', cleanQuery)
      .set('limit', String(limit))
      .set('type', type);

    return this.http.get<ApiResponse<SearchResult>>(this.searchUrl, { params }).pipe(
      map((res) => ({
        artists: res.data?.artists ?? [],
        songs: res.data?.songs ?? [],
        albums: res.data?.albums ?? []
      })),
      catchError((error) => {
        console.error('Search request failed:', error);
        return of({ artists: [], songs: [], albums: [] });
      })
    );
  }
}
